import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ApolloQueryResult } from '@apollo/client';
import { accountingTransferDrawerSections } from '@common/constants';
import {
  useCreateAccoutingTransfer,
  useGetAccountingTransferLovsList
} from '@common/slices';
import { AccountingTransferLookups } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useDynamicFormDrawerStyles } from '@components/dynamic-form/style';
import Loader from '@components/loader/Loader';
import { RootState } from '@core/redux/store';
import { GetAccountingTransfersResponse } from '@features/accounting/transfers/types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

type Props = {
  onSuccess?: (id: string) => void;
  companyId: string;
  refetch: () => Promise<ApolloQueryResult<GetAccountingTransfersResponse>>;
};

const AccountingTransferDrawer: React.FC<Props> = ({
  onSuccess,
  companyId,
  refetch
}: Props) => {
  const buttonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const { classes } = useDynamicFormDrawerStyles();

  const { data: lovsResp } = useGetAccountingTransferLovsList();
  const [createTransfer] = useCreateAccoutingTransfer(companyId);

  const [lookups, setLookups] = useState<AccountingTransferLookups | null>(
    null
  );

  const formik = useFormik({
    initialValues: {
      transferType: '',
      transferFrom: '',
      transferTo: ''
    },
    validationSchema: Yup.object({
      transferType: Yup.string().required('Required'),
      transferFrom: Yup.date().required('Required'),
      transferTo: Yup.date()
        .required('Required')
        .min(Yup.ref('transferFrom'), 'Must be on/after Transfer From')
    }),
    onSubmit: async (values) => {
      const toUTCMidnight = (dateStr: string) => {
        const d = new Date(dateStr);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');

        return `${y}-${m}-${day}T00:00:00.000Z`;
      };
      await createTransfer({
        createTransferInput: {
          ...values,
          transferFrom: toUTCMidnight(values.transferFrom),
          transferTo: toUTCMidnight(values.transferTo),
          companyId
        }
      }).then((d) => {
        if (!d.errors) {
          onSuccess(companyId);
          refetch();
        }
      });
    }
  });

  const loadLookups = useCallback(() => {
    const transferTypes = (
      lovsResp?.accounting?.getTransferTypeLookup || []
    ).reduce<Record<string, string>>((acc, el: any) => {
      acc[el.id] = el.title;

      return acc;
    }, {});
    setLookups({ transferTypes });
  }, [lovsResp]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const handleChange = useCallback(
    (fieldName: string, value: string | number | boolean) => {
      formik.setFieldValue(fieldName, value);
      formik.setFieldTouched(fieldName, true);
    },
    [formik]
  );

  const sections = useMemo(
    () => (lookups ? accountingTransferDrawerSections(formik, lookups) : []),
    [lookups, formik]
  );

  return !lookups ? (
    <div className={classes.loaderContainer}>
      <Loader />
    </div>
  ) : (
    <div>
      <DynamicFormWidget
        onChange={handleChange}
        sections={sections}
        disableForm={false}
        onSubmit={() => formik.handleSubmit()}
        submitButtonState={buttonState}
      />
    </div>
  );
};

export { AccountingTransferDrawer };
